import { useCallback } from 'react';
import { loadMediaMetadata } from '../helpers/uploadSelection';

export function useLocalQueueInteractions({
  interactionLocked,
  isUploadBlockedByCaseStatus,
  mediaItemsRef,
  caseFilesRef,
  mediaInputRef,
  transcriptInputRefs,
  setMediaItems,
  updateMediaItem,
  setInlineError,
  setCompletionDialog,
  setSelectionFeedback,
  getUploadLimitValidationMessage,
  classifyMediaFilesForSelection,
  createSelectionFeedbackItem,
  createSelectionSummaryDialog,
  createMediaItem,
  debugLog,
  getTranscriptFileValidationMessage,
  showUploadBlockedDialog,
  pendingDeleteItemId,
  setPendingDeleteItemId,
  pendingTranscriptDeleteItemId,
  setPendingTranscriptDeleteItemId,
  localDeleteInProgress,
  setLocalDeleteInProgress,
  transcriptDeleteInProgress,
  setTranscriptDeleteInProgress,
  loadCaseFiles
}) {
  const clearLocalQueueFeedback = useCallback(() => {
    setInlineError('');
    setCompletionDialog(null);
    setSelectionFeedback({ accepted: [], rejected: [] });
  }, [setCompletionDialog, setInlineError, setSelectionFeedback]);

  const appendMediaFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) {
      return;
    }

    const currentItems = mediaItemsRef.current;
    debugLog('append-media-files', {
      incomingCount: files.length,
      currentCount: currentItems.length,
      currentNames: currentItems.map((item) => item.mediaFile?.name).filter(Boolean),
      incomingNames: files.map((file) => file?.name).filter(Boolean)
    });

    const selectionResult = classifyMediaFilesForSelection(files, currentItems, caseFilesRef.current);
    const acceptedFeedback = selectionResult.acceptedFiles.map((file) => createSelectionFeedbackItem(file, 'Ready to upload.', 'success'));
    const shouldShowSelectionSummary = files.length > 1;
    const nextSelectionFeedback = {
      accepted: acceptedFeedback,
      rejected: []
    };

    setSelectionFeedback(nextSelectionFeedback);
    setInlineError('');
    setCompletionDialog(shouldShowSelectionSummary
      ? createSelectionSummaryDialog(acceptedFeedback, selectionResult.rejectedFiles)
      : null);

    if (selectionResult.rejectedFiles.length > 0) {
      if (!shouldShowSelectionSummary) {
        const firstRejectedReason = selectionResult.rejectedFiles[0]?.reason || 'Some files could not be added.';
        showUploadBlockedDialog(firstRejectedReason);
      }
    }

    if (selectionResult.acceptedFiles.length === 0) {
      return;
    }

    const metadataEntries = await Promise.all(
      selectionResult.acceptedFiles.map(async (file) => ({
        file,
        metadata: await loadMediaMetadata(file)
      }))
    );

    setMediaItems((currentItemsState) => {
      const nextItems = metadataEntries.map(({ file, metadata }, index) => ({
        ...createMediaItem(index, file),
        mediaMetadata: metadata
      }));
      const next = [...currentItemsState, ...nextItems];
      debugLog('append-media-files-commit', {
        previousCount: currentItemsState.length,
        addedCount: nextItems.length,
        rejectedCount: selectionResult.rejectedFiles.length,
        nextCount: next.length,
        nextNames: next.map((item) => item.mediaFile?.name).filter(Boolean)
      });
      mediaItemsRef.current = next;
      return next;
    });
  }, [caseFilesRef, classifyMediaFilesForSelection, createMediaItem, createSelectionFeedbackItem, createSelectionSummaryDialog, debugLog, mediaItemsRef, setCompletionDialog, setInlineError, setMediaItems, setSelectionFeedback]);

  const handleMediaFileChange = useCallback((event) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      event.target.value = '';
      return;
    }

    debugLog('media-file-change', {
      selectedNames: Array.from(event.target.files ?? []).map((file) => file?.name).filter(Boolean)
    });
    void appendMediaFiles(event.target.files);
    event.target.value = '';
  }, [appendMediaFiles, debugLog, interactionLocked, isUploadBlockedByCaseStatus]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    void appendMediaFiles(event.dataTransfer.files);
  }, [appendMediaFiles, interactionLocked, isUploadBlockedByCaseStatus]);

  const handleTranscriptToggle = useCallback((itemId, event) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      event.target.checked = false;
      return;
    }

    const checked = event.target.checked;
    clearLocalQueueFeedback();

    updateMediaItem(itemId, (item) => ({
      ...item,
      transcriptEnabled: checked,
      transcriptFile: checked ? item.transcriptFile : null,
      transcriptStoredFileName: checked ? item.transcriptStoredFileName : '',
      transcriptUploadStatus: checked ? item.transcriptUploadStatus : 'not-applicable',
      transcriptScanStatus: checked ? item.transcriptScanStatus : 'idle',
      transcriptFileId: checked ? item.transcriptFileId : '',
      latestError: ''
    }));

    if (!checked && transcriptInputRefs.current[itemId]) {
      transcriptInputRefs.current[itemId].value = '';
    }
  }, [clearLocalQueueFeedback, interactionLocked, isUploadBlockedByCaseStatus, transcriptInputRefs, updateMediaItem]);

  const handleTranscriptFileChange = useCallback((itemId, event) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      event.target.value = '';
      return;
    }

    const selectedFile = event.target.files?.[0] ?? null;
    if (selectedFile) {
      const transcriptValidationMessage = getTranscriptFileValidationMessage(selectedFile);
      if (transcriptValidationMessage) {
        setInlineError('');
        setCompletionDialog(null);
        setSelectionFeedback({ accepted: [], rejected: [] });
        showUploadBlockedDialog(transcriptValidationMessage);
        event.target.value = '';
        return;
      }
    }

    if (selectedFile) {
      const currentItems = mediaItemsRef.current;
      const nextItems = currentItems.map((item) => item.id === itemId
        ? {
          ...item,
          transcriptEnabled: true,
          transcriptFile: selectedFile,
          transcriptStoredFileName: selectedFile.name
        }
        : item);
      const limitMessage = getUploadLimitValidationMessage(nextItems, caseFilesRef.current);
      if (limitMessage) {
        setInlineError('');
        setCompletionDialog(null);
        setSelectionFeedback({ accepted: [], rejected: [] });
        showUploadBlockedDialog(limitMessage);
        event.target.value = '';
        return;
      }
    }

    setInlineError('');
    setCompletionDialog(null);
    setSelectionFeedback(selectedFile
      ? {
        accepted: [createSelectionFeedbackItem(selectedFile, 'Ready to upload.', 'success')],
        rejected: []
      }
      : { accepted: [], rejected: [] });
    updateMediaItem(itemId, (item) => ({
      ...item,
      transcriptFile: selectedFile,
      transcriptStoredFileName: selectedFile ? selectedFile.name : '',
      transcriptUploadStatus: selectedFile ? 'idle' : item.transcriptUploadStatus,
      transcriptScanStatus: selectedFile ? 'idle' : item.transcriptScanStatus,
      latestError: ''
    }));
  }, [caseFilesRef, createSelectionFeedbackItem, getTranscriptFileValidationMessage, getUploadLimitValidationMessage, interactionLocked, isUploadBlockedByCaseStatus, mediaItemsRef, setCompletionDialog, setInlineError, setSelectionFeedback, showUploadBlockedDialog, updateMediaItem]);

  const handleRemoveMediaFile = useCallback((itemId) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    clearLocalQueueFeedback();
    setMediaItems((currentItems) => {
      const next = currentItems.filter((item) => item.id !== itemId);
      debugLog('remove-media-file', {
        removedItemId: itemId,
        previousCount: currentItems.length,
        nextCount: next.length,
        nextNames: next.map((item) => item.mediaFile?.name).filter(Boolean)
      });
      mediaItemsRef.current = next;
      return next;
    });

    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }

    if (transcriptInputRefs.current[itemId]) {
      transcriptInputRefs.current[itemId].value = '';
      delete transcriptInputRefs.current[itemId];
    }
  }, [clearLocalQueueFeedback, debugLog, interactionLocked, isUploadBlockedByCaseStatus, mediaInputRef, mediaItemsRef, setMediaItems, transcriptInputRefs]);

  const handleRemoveTranscriptFile = useCallback((itemId) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    clearLocalQueueFeedback();
    updateMediaItem(itemId, (item) => ({
      ...item,
      transcriptEnabled: false,
      transcriptFile: null,
      transcriptStoredFileName: '',
      transcriptUploadStatus: 'not-applicable',
      transcriptScanStatus: 'idle',
      transcriptFileId: '',
      latestError: ''
    }));

    if (transcriptInputRefs.current[itemId]) {
      transcriptInputRefs.current[itemId].value = '';
    }
  }, [clearLocalQueueFeedback, interactionLocked, isUploadBlockedByCaseStatus, transcriptInputRefs, updateMediaItem]);

  const handleDescriptionChange = useCallback((itemId, value) => {
    updateMediaItem(itemId, (item) => ({
      ...item,
      description: value
    }));
  }, [updateMediaItem]);

  const handleTranscriptDescriptionChange = useCallback((itemId, value) => {
    updateMediaItem(itemId, (item) => ({
      ...item,
      transcriptDescription: value
    }));
  }, [updateMediaItem]);

  const confirmRemoveMediaFile = useCallback(async () => {
    if (interactionLocked || isUploadBlockedByCaseStatus || !pendingDeleteItemId || localDeleteInProgress) {
      return;
    }

    try {
      setLocalDeleteInProgress(true);
      handleRemoveMediaFile(pendingDeleteItemId);
      await loadCaseFiles(false);
      setPendingDeleteItemId(null);
    } finally {
      setLocalDeleteInProgress(false);
    }
  }, [handleRemoveMediaFile, interactionLocked, isUploadBlockedByCaseStatus, loadCaseFiles, localDeleteInProgress, pendingDeleteItemId, setLocalDeleteInProgress, setPendingDeleteItemId]);

  const confirmRemoveTranscriptFile = useCallback(async () => {
    if (interactionLocked || isUploadBlockedByCaseStatus || !pendingTranscriptDeleteItemId || transcriptDeleteInProgress) {
      return;
    }

    try {
      setTranscriptDeleteInProgress(true);
      handleRemoveTranscriptFile(pendingTranscriptDeleteItemId);
      await loadCaseFiles(false);
      setPendingTranscriptDeleteItemId(null);
    } finally {
      setTranscriptDeleteInProgress(false);
    }
  }, [handleRemoveTranscriptFile, interactionLocked, isUploadBlockedByCaseStatus, loadCaseFiles, pendingTranscriptDeleteItemId, setPendingTranscriptDeleteItemId, setTranscriptDeleteInProgress, transcriptDeleteInProgress]);

  return {
    appendMediaFiles,
    handleMediaFileChange,
    handleDrop,
    handleTranscriptToggle,
    handleTranscriptFileChange,
    handleRemoveMediaFile,
    handleRemoveTranscriptFile,
    handleDescriptionChange,
    handleTranscriptDescriptionChange,
    confirmRemoveMediaFile,
    confirmRemoveTranscriptFile
  };
}
